/**
 * El explorador del simulador (spike de I-18): una pregunta libre sobre la
 * fuente, que contesta un agente consultando un Bridge de solo lectura. Es
 * lo que el asesor le pediría a la vía «si no hay receta»: la métrica
 * exacta y el formato. Sin asesor por medio y sin casa: fuentes de demo.
 *
 * Por qué está hecha así:
 * - Sin prototipo propio: es un spike que cuelga del Simulador
 *   (simulador.html) y compone SOLO con @skein/ui, en la retícula del
 *   simulador (`sim-cols`). Si el spike sigue, pasa por /pantalla.
 * - NADA se persiste: la dirección y el token del Bridge viven en estado de
 *   React y viajan en cada petición; cerrar la pestaña los olvida. El token
 *   va en un campo de contraseña.
 * - La respuesta llega entera (hasta dos minutos y medio): mientras, la
 *   card del resultado dice que está explorando, con sus pestañas. Una
 *   pregunta nueva descarta lo que vuelva de la anterior. Al llegar, el
 *   foco va a la pestaña abierta (con una columna, el resultado queda bajo
 *   el pliegue).
 * - Las cuatro vistas del contrato: respuesta (con sus supuestos), camino
 *   (cada herramienta con su SQL y sus primeras filas, como secciones de
 *   la misma card), petición y consumo. La respuesta va SIEMPRE bajo el
 *   aviso de que no está verificada.
 * - El markdown del agente se pinta con `FichaProse` en el ritmo de
 *   lectura de la ficha: tokeniza y pinta elementos de React, nunca HTML.
 * - Los errores de la API van dentro de la card del resultado con
 *   «reintentar ahora»; los de la dirección del Bridge, junto a su campo.
 */
import { Fragment, useLayoutEffect, useRef, useState } from "react";
import type { ExplorarRespuesta, PasoExplorador } from "@skein/contracts";
import { ApiError } from "@skein/api-client";
import {
  Badge, Banner, Button, Card, CardHeader, CardPad, CodeBlock, Dl, Empty, FichaProse, Field, FieldGroup, Fainted, FormActs, Help, LiveNote, Mono, PageHead, Pill, PillGroup,
  Skeleton, Spinner, Sub, Tab, TabPanel, Table, Tabs, TdNum, Textarea, TextField, Th,
} from "@skein/ui";
import type { SimuladorApi } from "../simulador/Simulador";
import { diaCorto } from "../simulador/logica";

export interface ExploradorProps {
  api: Pick<SimuladorApi, "explorar">;
}

type Alcance = "curado" | "crudo";
type Vista = "respuesta" | "camino" | "peticion" | "consumo";

const VISTAS: ReadonlyArray<readonly [Vista, string]> = [["respuesta", "Respuesta"], ["camino", "Camino"], ["peticion", "Petición"], ["consumo", "Consumo"]];
const ALCANCES: ReadonlyArray<readonly [Alcance, string, string]> = [
  ["curado", "Curado", "Solo las vistas de Skein: nombres de negocio, saldos ya calculados y descripciones."],
  ["crudo", "Crudo", "También las tablas de Business Central tal cual: nombres con empresa y GUID, opciones como números."],
];
const HERRAMIENTA: Readonly<Record<PasoExplorador["herramienta"], string>> = { listar: "Listar objetos", describir: "Describir", consultar: "Consulta SQL" };
const FINAL: Readonly<Record<ExplorarRespuesta["final"], { texto: string; tone: "ok" | "warn" | "danger" }>> = {
  respondida: { texto: "respondida", tone: "ok" },
  agotada: { texto: "presupuesto agotado", tone: "warn" },
  fallida: { texto: "fallida", tone: "danger" },
};
const TOKEN_MIN = 32;

/** Preguntas de ejemplo sobre CRONUS: rellenan el formulario, no ejecutan. */
const EJEMPLOS: ReadonlyArray<{ titulo: string; pregunta: string; formato: string }> = [
  {
    titulo: "El viaje de una compra",
    pregunta: "Traza el viaje del albarán de compra 107023: qué se compró, a quién, y a qué clientes se vendió esa mercancía, con fechas, unidades, importe de venta y margen de cada venta.",
    formato: "Una tabla con: cliente, albarán de venta, factura de venta, fecha de venta, unidades, venta (€), coste (€), margen (€ y %). Debajo, cuántas unidades del albarán siguen en almacén.",
  },
  {
    titulo: "Deuda vencida por cliente",
    pregunta: "¿Qué clientes tienen deuda vencida hoy, cuánto y desde cuándo? Hoy es la fecha del último movimiento registrado.",
    formato: "Una tabla ordenada de mayor a menor: cliente, deuda vencida (€), deuda no vencida (€), factura vencida más antigua y días de retraso.",
  },
  {
    titulo: "Stock sin movimiento",
    pregunta: "¿Qué productos con existencias no han tenido ninguna venta en los últimos 90 días, y cuánto vale ese stock a coste?",
    formato: "Una tabla: producto, descripción, unidades en almacén, valor a coste (€), fecha de la última venta. Y el total.",
  },
];

const fmt = (n: number, d = 0) => n.toLocaleString("es-ES", { minimumFractionDigits: d, maximumFractionDigits: d });
const pasos = (n: number) => `${fmt(n)} ${n === 1 ? "paso" : "pasos"}`;
const FECHA = /^\d{4}-\d{2}-\d{2}(T00:00:00(\.000)?Z)?$/;
/** Una celda presentable: fechas cortas, sí/no, cifras con formato español. */
const celdaTexto = (v: string | number | boolean | null): string =>
  v === null ? "—" : typeof v === "boolean" ? (v ? "sí" : "no") : typeof v === "number" ? fmt(v, Number.isInteger(v) ? 0 : 2) : FECHA.test(v) ? diaCorto(v) : v;
const dinero = (importe: number, moneda: string, d: number) => `${fmt(importe, d)} ${moneda === "EUR" ? "€" : moneda === "USD" ? "US$" : moneda}`;

/** El mensaje de un fallo de la API para una persona: nunca «502 en /ruta». */
function mensajeApi(e: unknown): string {
  if (!(e instanceof ApiError)) return "No llego al Studio: la petición no ha salido.";
  if (e.status === 401 || e.status === 403) return "Tu cuenta no tiene permiso para explorar. Vuelve a entrar en el Studio o pide acceso a un admin de Skein.";
  if (e.message && !e.message.startsWith(`${e.status} en `)) return e.message;
  return e.status >= 500 ? "La API ha fallado al explorar. Si se repite, pásale a Martí la hora." : `La API ha rechazado la petición (${e.status}).`;
}

/** Un paso del camino, como sección de la card: qué pidió el agente, por qué, y lo que volvió. */
function Paso({ p }: { p: PasoExplorador }) {
  const sql = typeof p.entrada.sql === "string" ? p.entrada.sql.trim() : null;
  const objeto = typeof p.entrada.schema === "string" && typeof p.entrada.name === "string" ? `${p.entrada.schema}.${p.entrada.name}` : null;
  const busca = typeof p.entrada.search === "string" ? p.entrada.search : null;
  return (
    <>
      <CardHeader title={`${p.n} · ${HERRAMIENTA[p.herramienta]}`} badge={<Badge tone={p.ok ? undefined : "danger"}>{p.ok ? `${fmt(p.ms)} ms` : "error"}</Badge>} />
      <CardPad className="sim-form" aria-label={`Paso ${p.n}`} role="group">
        {p.nota !== undefined && <Sub>{p.nota}</Sub>}
        {objeto !== null && <span><Mono>{objeto}</Mono></span>}
        {busca !== null && <Help>Busca <Mono>{busca}</Mono></Help>}
        {sql !== null && <CodeBlock label={`SQL del paso ${p.n}`}>{sql}</CodeBlock>}
        {p.ok ? <Help>{p.resumen}</Help> : <Banner tone="danger" live={false}>{p.resumen}</Banner>}
        {p.ok && p.columnas !== undefined && p.filas !== undefined && p.filas.length > 0 && (
          <div>
            <Table dense label={`Filas del paso ${p.n}`} className="exp-filas">
              <thead><tr>{p.columnas.map((c) => <Th key={c} scope="col"><Mono>{c}</Mono></Th>)}</tr></thead>
              <tbody>{p.filas.map((f, i) => <tr key={i}>{f.map((v, j) => (typeof v === "number" ? <TdNum key={j}>{celdaTexto(v)}</TdNum> : <td key={j}>{celdaTexto(v)}</td>))}</tr>)}</tbody>
            </Table>
            {(p.filasTotal ?? 0) > p.filas.length && <Help>Se enseñan {fmt(p.filas.length)} de {fmt(p.filasTotal ?? 0)} filas; el agente las vio todas.</Help>}
          </div>
        )}
      </CardPad>
    </>
  );
}

export function Explorador({ api }: ExploradorProps) {
  const [url, setUrl] = useState("");
  const [token, setToken] = useState("");
  const [alcance, setAlcance] = useState<Alcance>("curado");
  const [pregunta, setPregunta] = useState("");
  const [formato, setFormato] = useState("");
  const [enVuelo, setEnVuelo] = useState(false);
  const [res, setRes] = useState<ExplorarRespuesta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorUrl, setErrorUrl] = useState<string | null>(null);
  const [vista, setVista] = useState<Vista>("respuesta");
  const [anuncio, setAnuncio] = useState("");
  const turno = useRef(0);
  const pestanas = useRef<Partial<Record<Vista, HTMLButtonElement | null>>>({});
  const foco = useRef<(() => void) | null>(null);

  // el foco se mueve DESPUÉS de pintar: la pestaña ya existe
  useLayoutEffect(() => {
    foco.current?.();
    foco.current = null;
  });

  const tokenCorto = token.trim() !== "" && token.trim().length < TOKEN_MIN;
  const faltaBridge = url.trim() === "" || token.trim().length < TOKEN_MIN;
  const faltaPregunta = pregunta.trim() === "";
  const porque = faltaBridge ? "Falta la dirección o el token del Bridge." : faltaPregunta ? "Falta la pregunta." : null;

  async function explorar() {
    if (enVuelo) return;
    if (porque !== null) {
      document.getElementById(url.trim() === "" ? "exp-url" : faltaBridge ? "exp-token" : "exp-pregunta")?.focus();
      return;
    }
    const mio = ++turno.current;
    setEnVuelo(true);
    setError(null);
    setErrorUrl(null);
    setAnuncio("Explorando: puede tardar hasta dos minutos.");
    try {
      const r = await api.explorar({ bridge: { url: url.trim(), token: token.trim() }, alcance, pregunta: pregunta.trim(), ...(formato.trim() ? { formato: formato.trim() } : {}) });
      if (mio !== turno.current) return;
      const v: Vista = r.final === "fallida" && r.camino.length > 0 ? "camino" : "respuesta";
      setRes(r);
      setVista(v);
      foco.current = () => pestanas.current[v]?.focus();
      setAnuncio(
        r.final === "respondida" ? `Exploración respondida en ${pasos(r.camino.length)}.`
        : r.final === "agotada" ? `Presupuesto agotado tras ${pasos(r.camino.length)}.`
        : "La exploración ha fallado.",
      );
    } catch (e) {
      if (mio !== turno.current) return;
      if (e instanceof ApiError && e.code === "invalid-bridge") {
        setErrorUrl(e.message);
        foco.current = () => document.getElementById("exp-url")?.focus();
      } else {
        setError(mensajeApi(e));
      }
      setAnuncio("");
    } finally {
      if (mio === turno.current) setEnVuelo(false);
    }
  }

  function usarEjemplo(e: (typeof EJEMPLOS)[number]) {
    setPregunta(e.pregunta);
    setFormato(e.formato);
    setAnuncio(`Pregunta de ejemplo cargada: ${e.titulo}.`);
  }

  const bridge = (
    <Card flush aria-label="Bridge">
      <CardHeader title="Bridge" hint="la dirección y el token del túnel" />
      <CardPad className="sim-form">
        <TextField id="exp-url" label="Dirección" value={url} placeholder="https://ejemplo-ruta.trycloudflare.com" autoComplete="off" spellCheck={false}
          help={<>La que imprime <Mono>cloudflared</Mono> al abrir el túnel.</>} {...(errorUrl !== null ? { error: errorUrl } : {})}
          onChange={(e) => { setUrl(e.target.value); setErrorUrl(null); }} />
        <TextField id="exp-token" label="Token" type="password" value={token} autoComplete="off"
          help={<><Mono>BRIDGE_TOKEN</Mono> de <Mono>apps/bridge/.env</Mono>. Viaja en cada petición y no se guarda.</>}
          {...(tokenCorto ? { error: `El token del Bridge tiene al menos ${TOKEN_MIN} caracteres.` } : {})}
          onChange={(e) => setToken(e.target.value)} />
        <FieldGroup id="exp-alcance" label="Alcance" help={ALCANCES.find(([a]) => a === alcance)?.[2]}>
          <PillGroup label="Alcance" describedBy="exp-alcance-h">
            {ALCANCES.map(([a, nombre]) => <Pill key={a} pressed={alcance === a} onClick={() => setAlcance(a)}>{nombre}</Pill>)}
          </PillGroup>
        </FieldGroup>
      </CardPad>
    </Card>
  );

  const peticion = (
    <Card flush aria-label="Pregunta">
      <CardHeader title="Pregunta" hint="lo que pediría el asesor" />
      <CardPad className="sim-form">
        <FieldGroup id="exp-ejemplos" label="Ejemplos sobre CRONUS" group help="Rellenan la pregunta y el formato; no exploran.">
          <span className="sim-addrow">
            {EJEMPLOS.map((e) => <Button key={e.titulo} variant="link" aria-describedby="exp-ejemplos-h" onClick={() => usarEjemplo(e)}>{e.titulo}</Button>)}
          </span>
        </FieldGroup>
        <Field id="exp-pregunta" label="Qué quieres saber" help="La métrica exacta: qué, de qué periodo, con qué desglose.">
          <Textarea id="exp-pregunta" rows={5} value={pregunta} aria-describedby="exp-pregunta-h" onChange={(e) => setPregunta(e.target.value)} />
        </Field>
        <Field id="exp-formato" label="Formato de la respuesta" help="Opcional: una cifra, una tabla con tales columnas…">
          <Textarea id="exp-formato" rows={3} value={formato} aria-describedby="exp-formato-h" onChange={(e) => setFormato(e.target.value)} />
        </Field>
        <FormActs>
          <Button variant="primary" loading={enVuelo} aria-disabled={enVuelo || porque !== null || undefined}
            aria-describedby={porque !== null ? "exp-porque" : undefined} tip={porque ?? undefined}
            onClick={() => void explorar()}>Explorar</Button>
          {porque !== null && <Help inline id="exp-porque">{porque}</Help>}
        </FormActs>
      </CardPad>
    </Card>
  );

  const pestanasBar = (r: ExplorarRespuesta | null) => (
    <Tabs className="sim-tabs" label="Vistas del resultado">
      {VISTAS.map(([v, nombre]) => (
        <Tab key={v} id={`exp-tab-${v}`} controls={`exp-panel-${v}`} selected={vista === v} ref={(el) => { pestanas.current[v] = el; }}
          onClick={() => { if (r !== null) setVista(v); }} mod={v === "camino" && r !== null ? String(r.camino.length) : undefined}>{nombre}</Tab>
      ))}
    </Tabs>
  );

  function resultado() {
    if (enVuelo) {
      return (
        <Card flush aria-label="Resultado" aria-busy="true">
          <CardHeader title="Resultado" />
          {pestanasBar(null)}
          <CardPad>
            <div className="sim-busy sim-gap"><Spinner /><Help inline>Explorando la fuente. Puede tardar hasta dos minutos.</Help></div>
            <div className="sim-skel" aria-hidden="true">
              <Skeleton className="sim-skel-l sim-skel-w60" /><Skeleton className="sim-skel-l sim-skel-w80" /><Skeleton className="sim-skel-block" />
            </div>
          </CardPad>
        </Card>
      );
    }
    if (error !== null) {
      return (
        <Card flush aria-label="Resultado">
          <CardHeader title="Resultado" />
          <CardPad>
            <Banner tone="danger" live={false}>
              <b>No se ha podido explorar.</b> {error}{" "}
              <Button variant="link" onClick={() => void explorar()}>reintentar ahora</Button>
            </Banner>
          </CardPad>
        </Card>
      );
    }
    if (res === null) {
      return (
        <Card flush aria-label="Resultado">
          <CardPad>
            <Empty inline title="Sin exploración aún.">
              Abre el túnel del Bridge, pega su dirección y su token, escribe la pregunta y pulsa «Explorar». El agente consulta la base y te enseña la respuesta, cada consulta que hizo y lo que costó.
            </Empty>
          </CardPad>
        </Card>
      );
    }
    const r = res;
    const c = r.consumo;
    return (
      <Card flush aria-label="Resultado">
        <CardHeader title="Resultado" badge={<Badge tone={FINAL[r.final].tone}>{FINAL[r.final].texto}</Badge>}
          hint={`${pasos(r.camino.length)} · ${fmt(c.ms / 1000, 1)} s${c.importe !== null && c.moneda !== null ? ` · ${dinero(c.importe, c.moneda, 2)}` : ""}`} />
        {pestanasBar(r)}
        <TabPanel id="exp-panel-respuesta" labelledBy="exp-tab-respuesta" hidden={vista !== "respuesta"}>
          <CardPad>
            {r.final === "fallida" && <Banner tone="danger" live={false} className="sim-gap"><b>La exploración ha fallado.</b> {r.fallo?.mensaje ?? "Sin detalle del fallo."}</Banner>}
            {r.final === "agotada" && <Banner tone="warn" live={false} className="sim-gap"><b>Presupuesto agotado.</b> El agente ha cerrado con lo que tenía: puede faltar comprobación.</Banner>}
            {r.respuesta !== null ? (
              <>
                <Banner tone="warn" live={false} className="sim-gap"><b>Exploración, no verificada.</b> Es lo que un agente ha deducido consultando la base; no es una cifra del catálogo. El camino enseña cada consulta.</Banner>
                <FichaProse className="lectura" markdown={r.respuesta} />
              </>
            ) : r.final !== "fallida" && (
              <Empty inline title="Sin respuesta.">El agente no llegó a escribir una respuesta; el camino enseña lo que consultó.</Empty>
            )}
          </CardPad>
        </TabPanel>
        <TabPanel id="exp-panel-camino" labelledBy="exp-tab-camino" hidden={vista !== "camino"}>
          {r.camino.length === 0
            ? <CardPad><Empty inline title="Sin pasos.">El agente no llegó a consultar la fuente.</Empty></CardPad>
            : r.camino.map((p) => <Fragment key={p.n}><Paso p={p} /></Fragment>)}
        </TabPanel>
        <TabPanel id="exp-panel-peticion" labelledBy="exp-tab-peticion" hidden={vista !== "peticion"}>
          <CardPad>
            <Dl items={[
              { dt: "Pregunta", dd: r.peticion.pregunta },
              { dt: "Formato", dd: r.peticion.formato ?? "—" },
              { dt: "Alcance", dd: ALCANCES.find(([a]) => a === r.peticion.alcance)?.[1] ?? r.peticion.alcance },
              { dt: "Base de datos", dd: r.peticion.baseDeDatos !== null ? <Mono>{r.peticion.baseDeDatos}</Mono> : "—" },
            ]} />
          </CardPad>
        </TabPanel>
        <TabPanel id="exp-panel-consumo" labelledBy="exp-tab-consumo" hidden={vista !== "consumo"}>
          <CardPad>
            <Dl items={[
              { dt: "Modelo", dd: <Mono>{c.modelo}</Mono> },
              { dt: "Vueltas", dd: fmt(c.vueltas) },
              { dt: "Consultas SQL", dd: fmt(c.consultas) },
              { dt: "Tokens de entrada", dd: fmt(c.tokensEntrada) },
              { dt: "Tokens de salida", dd: fmt(c.tokensSalida) },
              { dt: "Caché leída / escrita", dd: `${fmt(c.tokensCacheLeidos)} / ${fmt(c.tokensCacheEscritos)}` },
              { dt: "Importe", dd: c.importe !== null && c.moneda !== null ? dinero(c.importe, c.moneda, 4) : "sin precio para este modelo" },
              { dt: "Tiempo", dd: `${fmt(c.ms / 1000, 1)} s` },
            ]} />
          </CardPad>
        </TabPanel>
      </Card>
    );
  }

  return (
    <>
      <PageHead
        title="Explorar sin receta"
        meta={<>
          <Badge>Bridge de solo lectura</Badge>
          <Badge tone="accent">nada se guarda</Badge>
          <Fainted className="sim-ph-note">lo que el asesor pediría cuando ninguna receta tiene el dato · prueba</Fainted>
        </>}
      />
      <div className="sim-cols">
        <div className="sim-stack">
          {bridge}
          {peticion}
        </div>
        <div className="sim-stack">{resultado()}</div>
      </div>
      <LiveNote>{anuncio}</LiveNote>
    </>
  );
}
